import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProcessTimeline = ({ processes, activeProcess, onProcessChange }) => {
  const [hoveredStep, setHoveredStep] = useState(null);

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-headline font-bold text-xl text-foreground">
          Process Timeline
        </h3>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={16} className="text-muted-foreground" />
          <span className="font-body text-sm text-muted-foreground">
            Interactive Journey
          </span>
        </div>
      </div>
      {/* Timeline Scrubber */}
      <div className="relative mb-8">
        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-border transform -translate-y-1/2"></div>
        <div 
          className="absolute top-1/2 left-0 h-0.5 bg-accent transform -translate-y-1/2 transition-all duration-500"
          style={{ width: `${((activeProcess + 1) / processes?.length) * 100}%` }}
        ></div>
        
        <div className="relative flex justify-between">
          {processes?.map((process, index) => (
            <button
              key={index}
              className={`relative w-8 h-8 rounded-full border-2 transition-all duration-300 ${
                index <= activeProcess
                  ? 'bg-accent border-accent text-background' :'bg-background border-border text-muted-foreground hover:border-accent/50'
              }`}
              onClick={() => onProcessChange(index)}
              onMouseEnter={() => setHoveredStep(index)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <Icon name={process.icon} size={16} />
              
              {hoveredStep === index && (
                <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-popover text-popover-foreground px-3 py-2 rounded-lg text-xs font-body shadow-lg border border-border/20 whitespace-nowrap">
                    {process.name}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover"></div>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Current Process Details */}
      <div className="space-y-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
            <Icon name={processes?.[activeProcess]?.icon} size={24} className="text-accent" />
          </div>
          <div>
            <h4 className="font-headline font-bold text-lg text-foreground">
              {processes?.[activeProcess]?.name}
            </h4>
            <p className="font-body text-sm text-muted-foreground">
              {processes?.[activeProcess]?.description}
            </p>
          </div>
        </div>

        {/* Process Steps */}
        <div className="space-y-3">
          {processes?.[activeProcess]?.steps?.map((step, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-surface/30">
              <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-accent">{index + 1}</span>
              </div>
              <div>
                <h5 className="font-body font-medium text-sm text-foreground mb-1">
                  {step?.title}
                </h5>
                <p className="font-body text-xs text-muted-foreground">
                  {step?.description}
                </p>
                {step?.duration && (
                  <div className="flex items-center space-x-1 mt-2">
                    <Icon name="Clock" size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{step?.duration}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-border/20">
          <Button
            variant="outline"
            size="sm"
            disabled={activeProcess === 0}
            onClick={() => onProcessChange(activeProcess - 1)}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-2">
            <span className="font-body text-sm text-muted-foreground">
              {activeProcess + 1} of {processes?.length}
            </span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            disabled={activeProcess === processes?.length - 1}
            onClick={() => onProcessChange(activeProcess + 1)}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProcessTimeline;